/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator).*/
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (cleanup button in profile).*/
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; profile + list-dialog SHARE the cache).*/
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated).*/
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user.*/
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id).*/
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme).*/
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
  },
  /** Custodia (P-10) — supervision domain (parental control).*/
  supervision: {
    all: ["supervision"] as const,
    /** Local list of supervised profiles for the guardian.*/
    profiles: (userId: string | undefined) => ["supervision", "profiles", userId] as const,
    /** Specific profile (local replica).*/
    profile: (profileId: string | undefined) => ["supervision", "profile", profileId] as const,
    /** Activity report (API, seals the view in guardian log).*/
    report: (profileId: string | undefined) => ["supervision", "report", profileId] as const,
    /** Guardian audit log (API).*/
    audit: (profileId: string | undefined) => ["supervision", "audit", profileId] as const,
    /** Hash chain integrity verification (API).*/
    verify: (profileId: string | undefined) => ["supervision", "verify", profileId] as const,
    /** Transparency notice seen by the supervised subject (API).*/
    transparency: (profileId: string | undefined) =>
      ["supervision", "transparency", profileId] as const,
    /** Enrolled devices from agent datapath (API).*/
    devices: (profileId: string | undefined) => ["supervision", "devices", profileId] as const,
    /** Opt-in locations of the supervised subject (API, seals view).*/
    locations: (profileId: string | undefined) => ["supervision", "locations", profileId] as const,
    /** Classrooms (aulas) of school multi-org (local replica, org-scoped).*/
    aulas: (userId: string | undefined) => ["supervision", "aulas", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences).*/
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector).*/
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector).*/
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all?*/
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync).*/
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists.*/
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters.*/
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  organizations: {
    /** Local replica (PowerSync) of my organizations.*/
    localList: () => ["organizations", "local-list"] as const,
    /** Members (with public info) of an organization, from the local replica.*/
    localMembers: (organizationId: string | undefined) =>
      ["organizations", "local-members", organizationId] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
